import { XIcon } from '@phosphor-icons/react'
import { match } from 'ts-pattern'
import { type UseNewerScanBannerResult, useNewerScanBanner } from '../../hooks/use-newer-scan-banner.ts'
import { type Tool } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './newer-scan-banner.css.ts'

export type NewerScanBannerState = 'imported' | 'analyzing' | 'has-analysis'

export type NewerScanBannerPureProps = {
  state: NewerScanBannerState
  completedAnalysis: number
  total: number
  onView: () => unknown
  onDismiss: () => unknown
  viewLabel?: string
}

export const NewerScanBannerPure: React.FC<NewerScanBannerPureProps> = ({
  state,
  completedAnalysis,
  total,
  onView,
  onDismiss,
  viewLabel = 'View',
}) => {
  const isDismissible = state === 'imported' || state === 'analyzing'

  const messageText = match(state)
    .with('imported', () => 'A newer scan has been imported')
    .with('analyzing', () => 'A newer scan is being analyzed')
    .with('has-analysis', () => 'A newer scan has analysis')
    .exhaustive()

  return (
    <div className={styles.container} role="status" aria-label="Newer scan notification">
      <div className={styles.dot({ state })} aria-hidden="true" />
      <span className={styles.message}>{messageText}</span>
      {state === 'has-analysis' && (
        <span className={styles.count}>
          {completedAnalysis}/{total} {pluralize(total, 'finding', 'findings')}
        </span>
      )}
      <button className={styles.viewButton} onClick={onView}>
        {viewLabel}
      </button>
      {isDismissible && (
        <button className={styles.dismissButton} onClick={onDismiss} aria-label="Dismiss newer scan notification">
          <XIcon size={12} />
        </button>
      )}
    </div>
  )
}

type NewerScanBannerProps = {
  currentScanId: string
  repositoryId: string
  tool: Tool
  onView: (newerScan: Pick<UseNewerScanBannerResult, 'newerScanId' | 'state'>) => unknown
  viewLabel?: string
}

export const NewerScanBanner: React.FC<NewerScanBannerProps> = ({
  currentScanId,
  repositoryId,
  tool,
  onView,
  viewLabel,
}) => {
  const newerScan = useNewerScanBanner({ currentScanId, repositoryId, tool })
  if (!newerScan) return null
  if (newerScan.isDismissed && newerScan.state !== 'has-analysis') return null
  return (
    <NewerScanBannerPure
      state={newerScan.state}
      completedAnalysis={newerScan.completedAnalysis}
      total={newerScan.total}
      onView={() => onView({ newerScanId: newerScan.newerScanId, state: newerScan.state })}
      onDismiss={newerScan.dismiss}
      viewLabel={viewLabel}
    />
  )
}
